/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps, RefObject, JSX } from 'preact';
import { useRef } from 'preact/hooks';

import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';

import { Flex } from '#UNSAFE_Flex';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { LiveRegion } from '#UNSAFE_LiveRegion';
import type { TextFieldInputVariantOptions } from '#UNSAFE_TextField/themes/TextFieldInputStyles.css';
import { TextFieldInputRedwoodTheme } from '#UNSAFE_TextField/themes/redwood/TextFieldInputTheme';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { useUser } from '#hooks/UNSAFE_useUser';
import type { BundleType } from '#resources/nls/bundle';
import { isEmbeddedVariant } from '#utils/PRIVATE_formControlUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { formatTimeFromMasks } from '#utils/UNSAFE_timeUtils';
import { InputTimeMask } from './InputTimeMask';
import { LiteralSegment } from './LiteralSegment';
import { TimeSegment } from './TimeSegment';
import type { EditableTimeSegmentType, TimePlaceholders } from './types';
import { useTimeFieldHandlers } from './useTimeFieldHandlers';
import { TimeFieldState, TimeSegmentState } from './getTimeFieldInitialState';
import { useTimeReset } from './useTimeReset';
import type { TimeReducerAction } from './timeReducer';
import type { Dispatch } from 'preact/hooks';
import { useTimeFieldCommitInput } from './useTimeFieldCommitInput';

type InputTimeMaskProps = ComponentProps<typeof InputTimeMask>;

type AriaProps = Pick<
  UnpackSignals<JSX.AriaAttributes>,
  'aria-describedby' | 'aria-invalid' | 'aria-label' | 'aria-labelledby'
>;

type HTMLElementProps = Pick<UnpackSignals<JSX.HTMLAttributes<HTMLElement>>, 'onBlur' | 'onFocus'>;

export type Props = AriaProps &
  TestIdProps &
  HTMLElementProps & {
    /**
     * A base id prefix.
     */
    baseId: string;

    /**
     * The reducer's dispatch function. The dispatch function lets you update the state to a different value and trigger a re-render.
     */
    dispatch: Dispatch<TimeReducerAction>;

    /**
     * A ref to the element that receives focus when the field's label is clicked,
     * or when the component is toggled between readonly and enabled. This is the
     * first segment in the field.
     */
    fieldRef?: RefObject<HTMLDivElement>;

    /**
     * Specifies the smallest time unit that is displayed by the component.
     * If set to minute, only hour and minute are shown.
     * If set to second then hour, minute, and second are shown.
     * If set to millisecond then hour, minute, second and millisecond are shown.
     */
    granularity?: InputTimeMaskProps['granularity'];

    /**
     * Whether to show a leading zero in the hour field when there is only one digit or not.
     */
    hasLeadingZeroForHour: boolean;

    /**
     * True if the component has an inside label.
     */
    hasInsideLabel?: boolean;

    /**
     * Specifies for accessibility purposes whether a value is required.
     */
    isRequired?: InputTimeMaskProps['isRequired'];

    /**
     * The placeholder masks used by the time field.
     */
    masks: TimePlaceholders;

    /**
     * The state for the TimeField.
     */
    state: TimeFieldState;

    /**
     * Specifies how to align text within the field.
     */
    textAlign?: InputTimeMaskProps['textAlign'];

    /**
     * The current value of the component. The value's hour is a 0-23 hour.
     */
    value?: InputTimeMaskProps['value'];

    /**
     * The style variant of the component.
     */
    variant?: InputTimeMaskProps['variant'];

    /**
     * Callback invoked each time the user changes the value of a segment.
     *
     * For example, if the user
     * types '12' into the minute segment of an empty time field,
     * this callback will be called twice to change the minute
     * first to 1 and then 12.
     *
     * If the user types '1' into the hour segment of an empty time field,
     * this callback will be called once with the hour set to 1.
     * If the user then types '2', the callback will be called again. What it gets called with
     * depends on if the InputTimeMask is displaying a 1-12 hour time with a day period or a 0-23 hour time.
     * This is because the InputTimeMask's value's hour is stored as a 0-23 hour.
     *
     * If the user types in '12', and the time has a day period, the value's hour is 0, since 12 AM is converted to hour 0.
     * If the user types in '12', and the time does not have a day period, the value's hour is 12.
     *
     * If the user types in an hour that completes the segment, the other segments that are empty
     * are automatically filled with 0s, and the callback's value will reflect that, e.g.,
     * {hour: 0, minute: 0}.
     */
    onInput: InputTimeMaskProps['onInput'];

    /**
     * Callback invoked when the user commits the entered value by blurring or hitting the enter key.
     * The value's hour is a 0-23 hour.
     *
     * If the InputTimeMask's hour field is showing a 1-12 hour time, the
     * field's display value will be converted to a 0-23 hour, using the dayPeriod segment's value, which defaults to 'AM'.
     */
    onCommit: InputTimeMaskProps['onCommit'];
  };

const findSegment = (
  segments: TimeSegmentState[],
  type: EditableTimeSegmentType
): TimeSegmentState => {
  return segments.find((segment) => segment.type === type)!;
};

/**
 * Container for Segment children that represent different parts of a time with no date.
 */
const TimeField = ({
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  baseId,
  dispatch,
  fieldRef,
  granularity = 'minute',
  hasLeadingZeroForHour,
  hasInsideLabel,
  isRequired,
  masks: timeMasks,
  onBlur,
  onCommit,
  onFocus,
  onInput,
  state,
  testId,
  textAlign,
  value,
  variant = 'default'
}: Props) => {
  const { direction, locale } = useUser();

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const announceTimeCleared = translations.inputTimeMask_time_cleared();

  const { isDisabled, isFocused, isInputFocused } = useFormFieldContext();

  const hasValue = value !== undefined;

  // TimeField is similar to TextFieldInput for styling. The main difference
  // is TextFieldInput renders a textarea or input and TimeField renders a div, so the positioning with
  // padding is different. TimeField and TextFieldInput are both the mainContent
  // of a TextField component.
  const { classes } = useComponentTheme<TextFieldInputVariantOptions>(TextFieldInputRedwoodTheme, {
    type: 'notPassword',
    styleVariant: isEmbeddedVariant(variant) ? 'embedded' : 'default',
    textarea: 'notTextArea',
    input: 'notInput',
    prefix: 'noPrefix',
    suffix: 'noSuffix',
    startContent: 'noStartContent',
    endContent: 'noEndContent',
    insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
    value: hasValue ? 'hasValue' : 'noValue',
    focused: isFocused ? 'isFocused' : 'notFocused',
    disabled: isDisabled ? 'isDisabled' : 'notDisabled'
  });

  const compClasses = classNames([classes]);

  const { isTimeSelected, isPartialTime, segments: editableSegments } = state;
  const hourSegment = findSegment(editableSegments, 'hour');
  const minuteSegment = findSegment(editableSegments, 'minute');
  const secondSegment =
    granularity === 'second' || granularity === 'millisecond'
      ? findSegment(editableSegments, 'second')
      : undefined;
  const millisecondSegment =
    granularity === 'millisecond' ? findSegment(editableSegments, 'millisecond') : undefined;
  const dayPeriodSegment = findSegment(editableSegments, 'dayPeriod');

  const segmentsMap = {
    hour: hourSegment,
    minute: minuteSegment,
    second: secondSegment,
    millisecond: millisecondSegment,
    dayPeriod: dayPeriodSegment
  };

  const groupRef = useRef<HTMLDivElement>(null);

  const { timeFieldHandlers, timeClearedRef, segmentHandlers, tabIndex } = useTimeFieldHandlers({
    direction,
    dispatch,
    groupRef,
    isDisabled,
    isInputFocused,
    state
  });

  const { timeResetRef } = useTimeReset({
    dispatch,
    granularity,
    hasLeadingZeroForHour,
    locale,
    state,
    timeMasks,
    value
  });

  useTimeFieldCommitInput({
    timeResetRef,
    dispatch,
    onCommit,
    onInput,
    state,
    value
  });

  // Like other form components, don't show placeholders for inside labels unless focused.
  // This flag causes the segment placeholders to be hidden.
  const isHidden = value === undefined && hasInsideLabel && !isFocused;

  // If the time is complete, create a hidden accessible region with the full formatted time.
  const formattedTimeStr = !state.isCompleteTime
    ? ''
    : formatTimeFromMasks(
        locale,
        state.currentTime!,
        granularity,
        timeMasks,
        hasLeadingZeroForHour
      );

  const formattedTimeAriaId = `${baseId}-time`;
  const formattedTimeAria = state.isCompleteTime ? (
    <HiddenAccessible id={formattedTimeAriaId}>{formattedTimeStr}</HiddenAccessible>
  ) : undefined;
  const ariaDescribedBy = state.isCompleteTime
    ? merge([describedBy, formattedTimeAriaId])
    : describedBy;

  // Add an announcement to the live region if the time was cleared.
  // Note: we deliberately do not reset timeClearedRef because there are multiple
  // rerenders after clearing the time and we want to make sure the announcement
  // has time to be heard.
  const announceTimeClearedText = timeClearedRef.current ? announceTimeCleared : '';
  const announceAutoFilledTime = state.isTimeAutoFilled ? formattedTimeStr : '';
  // firstSegmentType is used to determine on what TimeSegment to put the fieldRef,
  // and fieldRef is a ref to the element that receives focus when the field's label is clicked.
  const firstSegmentType = state.orderedSegments[0];

  return (
    <div
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      class={compClasses}
      onFocusIn={onFocus}
      onFocusOut={onBlur}
      ref={groupRef}
      role="group"
      tabIndex={tabIndex}
      {...timeFieldHandlers}>
      <Flex direction="row" wrap="nowrap" justify={textAlign} height="100%" align="center">
        {timeMasks.map(({ type, value: placeholder }) => {
          if (type === 'literal') {
            return (
              <LiteralSegment
                isHidden={isHidden}
                isHighlighted={isTimeSelected}
                isPlaceholder={!isPartialTime}
                text={placeholder}></LiteralSegment>
            );
          } else if (segmentsMap[type] !== undefined) {
            const segmentProps = segmentsMap[type];

            return (
              <TimeSegment
                inputRef={type === firstSegmentType ? fieldRef : undefined}
                isDisabled={isDisabled}
                isFieldFocused={isFocused}
                isInvalid={!!ariaInvalid}
                isHidden={isHidden}
                isHighlighted={isTimeSelected}
                isRequired={isRequired}
                placeholder={placeholder}
                {...segmentHandlers}
                {...segmentProps}></TimeSegment>
            );
          }
          return null;
        })}
      </Flex>
      {formattedTimeAria}
      {!isDisabled && <LiveRegion>{announceTimeClearedText}</LiveRegion>}
      {!isDisabled && (
        <LiveRegion testId={testId ? testId + '_autoFilledTime' : undefined}>
          {announceAutoFilledTime}
        </LiveRegion>
      )}
    </div>
  );
};

export { findSegment, TimeField };
